import type {
  AnswersByKey,
  EmailStep,
  FlowVariant,
  FunnelOption,
  FunnelOptionFeedback,
  FunnelStep,
  LoadingStep,
  PaymentPlanOption,
  PaymentStep,
  QuestionStep,
  ResultStep,
  WelcomeStep
} from "./types";
import { config } from "../config";

/**
 * Single source of truth for plan pricing.
 * Update prices here only. Original price is derived as totalPrice / (1 - savePercent/100).
 * Per-week is derived as totalPrice / weeksInPeriod.
 */
export const LUMI_PLANS: PaymentPlanOption[] = [
  {
    id: "monthly",
    label: "1 month",
    savePercent: 40,
    weeksInPeriod: 4.33,
    totalPrice: "$11"
  },
  {
    id: "quarterly",
    label: "3 months",
    savePercent: 50,
    weeksInPeriod: 13,
    totalPrice: "$24.99",
    isMostPopular: true
  },
  {
    id: "annual",
    label: "1 year",
    savePercent: 60,
    weeksInPeriod: 52,
    totalPrice: "$49"
  }
];

type FlowValue<T> = T | { a: T; b: T };

const split = <T,>(a: T, b: T): FlowValue<T> => ({ a, b });

const isSplitValue = <T,>(value: FlowValue<T>): value is { a: T; b: T } => {
  return typeof value === "object" && value !== null && "a" in value && "b" in value;
};

const resolveByFlow = <T,>(value: FlowValue<T>, flow: FlowVariant): T => {
  return isSplitValue(value) ? value[flow] : value;
};

type RawWelcomeStep = Omit<
  WelcomeStep,
  "title" | "description" | "ratingText" | "confirmLabel" | "heroSlides" | "featurePills"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  ratingText: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroSlides?: FlowValue<WelcomeStep["heroSlides"]>;
  featurePills?: FlowValue<WelcomeStep["featurePills"]>;
  finePrint?: FlowValue<string>;
};

type RawQuestionStep = Omit<
  QuestionStep,
  "title" | "subtitle" | "helperText" | "options" | "mediaLabel" | "mediaImage"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  helperText?: FlowValue<string>;
  options: FlowValue<FunnelOption[]>;
  mediaLabel?: FlowValue<string>;
  mediaImage?: FlowValue<string>;
  optionFeedback?: Record<string, FunnelOptionFeedback>;
};

type RawAffirmationStep = Omit<
  Extract<FunnelStep, { kind: "affirmation" }>,
  "title" | "description" | "confirmLabel" | "mediaLabel" | "heroImage"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  heroImage?: FlowValue<string>;
  mediaLabel?: FlowValue<string>;
};

type RawLoadingStep = Omit<LoadingStep, "title" | "subtitle" | "confirmLabel" | "loadingFact"> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  loadingFact: FlowValue<string>;
  heroImage?: FlowValue<string>;
};

type RawResultStep = Omit<
  ResultStep,
  "title" | "subtitle" | "personaName" | "description" | "questMap" | "confirmLabel"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  personaName: FlowValue<string>;
  description: FlowValue<string[]>;
  questMap: FlowValue<ResultStep["questMap"]>;
  confirmLabel: FlowValue<string>;
};

type RawEmailStep = Omit<
  EmailStep,
  "title" | "subtitle" | "description" | "confirmLabel" | "secondaryCta"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  secondaryCta?: FlowValue<string>;
};

type RawPaymentStep = Omit<
  PaymentStep,
  | "title"
  | "subtitle"
  | "description"
  | "benefits"
  | "plans"
  | "proofBullets"
  | "testimonials"
  | "confirmLabel"
  | "whyCardCopy"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  benefits: FlowValue<string[]>;
  plans: FlowValue<PaymentStep["plans"]>;
  proofBullets: FlowValue<string[]>;
  testimonials: FlowValue<PaymentStep["testimonials"]>;
  confirmLabel: FlowValue<string>;
  whyCardCopy: FlowValue<string>;
};

type RawStep =
  | RawWelcomeStep
  | RawQuestionStep
  | RawAffirmationStep
  | RawLoadingStep
  | RawResultStep
  | RawEmailStep
  | RawPaymentStep;

const yesNoOptions: FunnelOption[] = [
  { id: "yes", label: "Yes", emoji: "✅" },
  { id: "no", label: "No", emoji: "❌" }
];

// ─── Persona system ──────────────────────────────────────────────────────────

type PersonaKey =
  | "spark_chaser"
  | "apprentice"
  | "ranger"
  | "navigator"
  | "sage"
  | "devoted_scholar";

type PersonaData = {
  heroImage: string;
  personaName: string;
  description: string[];
  questMap: ResultStep["questMap"];
};

type PersonaCopy = Omit<PersonaData, "questMap">;

const PERSONAS: Record<PersonaKey, PersonaCopy> = {
  spark_chaser: {
    heroImage: "/persona_spark_chaser.webp",
    personaName: "The Spark Chaser",
    description: [
      "You're driven by sudden curiosity — you learn when something catches your eye, not on a schedule, and that's a genuine strength. Lumi is built for exactly this. Short, brilliant sparks of knowledge whenever curiosity strikes."
    ]
  },
  apprentice: {
    heroImage: "/persona_apprentice.webp",
    personaName: "The Apprentice",
    description: [
      "You value showing up consistently over chasing depth — you know that small, steady effort always beats intensity in the long run. Lumi will meet you with short, well-structured sessions that quietly stack up over time."
    ]
  },
  ranger: {
    heroImage: "/persona_ranger.webp",
    personaName: "The Ranger",
    description: [
      "You're endlessly curious with wide-ranging interests and the instinct to follow whatever grabs your attention next. Lumi gives you the right trail to start on — after that, you're free to roam."
    ]
  },
  navigator: {
    heroImage: "/persona_navigator.webp",
    personaName: "The Navigator",
    description: [
      "You're genuinely curious and you like having a clear direction — you want to know that every step is taking you somewhere meaningful. Lumi builds your path intelligently — purposeful quests that build on each other."
    ]
  },
  sage: {
    heroImage: "/persona_sage.webp",
    personaName: "The Sage",
    description: [
      "You go deep, roam wide, and follow ideas wherever they lead — time disappears when something is genuinely interesting. Lumi is made for minds that refuse to stay on the surface."
    ]
  },
  devoted_scholar: {
    heroImage: "/persona_devoted_scholar.webp",
    personaName: "The Devoted Scholar",
    description: [
      "You don't just want to know things — you want to understand them properly, and you hold yourself to a high standard. Lumi builds your curriculum methodically, with the depth and rigour your mind demands."
    ]
  }
};

export const getPersonaHeroImages = (): string[] => {
  return Object.values(PERSONAS).map((persona) => persona.heroImage);
};

const PERSONA_ALIASES: Record<string, PersonaKey> = {
  spark_chaser: "spark_chaser",
  sparkchaser: "spark_chaser",
  spark: "spark_chaser",
  apprentice: "apprentice",
  ranger: "ranger",
  navigator: "navigator",
  sage: "sage",
  devoted_scholar: "devoted_scholar",
  devotedscholar: "devoted_scholar",
  scholar: "devoted_scholar"
};

const normalizePersonaAlias = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "_");
};

const resolvePersonaOverride = (value: string | null): PersonaKey | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizePersonaAlias(value);
  return PERSONA_ALIASES[normalized] ?? null;
};

const str = (answers: AnswersByKey, key: string): string =>
  typeof answers[key] === "string" ? answers[key] : "";

const arr = (answers: AnswersByKey, key: string): string[] =>
  Array.isArray(answers[key]) ? (answers[key] as string[]) : [];

const computeTraitMap = (answers: AnswersByKey): ResultStep["questMap"] => {
  // ── Curiosity ──────────────────────────────────────────────────────────
  const rabbitHoleCuriosity: Record<string, number> = {
    rarely: 0, sometimes: 1, often: 2, always: 3
  };
  const topicCount = Math.min(arr(answers, "topic_interest_grid").length, 12);
  const curiosityRaw =
    (rabbitHoleCuriosity[str(answers, "rabbit_hole")] ?? 1) +
    Math.round(topicCount / 3) +
    (str(answers, "rapid_fire_1") === "yes" ? 1 : 0) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0) +
    (arr(answers, "your_why").includes("curiosity") ? 1 : 0) +
    (arr(answers, "your_why").includes("understand_world") ? 1 : 0) +
    (arr(answers, "your_why").includes("conversation") ? 1 : 0);

  // ── Drive ──────────────────────────────────────────────────────────────
  const dailyTimeDrive: Record<string, number> = {
    "2_min": 0, "5_min": 1, "10_min": 3, pockets: 1
  };
  const questDiffDrive: Record<string, number> = {
    explorer: 0, adventurer: 1, scholar: 3
  };
  const scrollDrive: Record<string, number> = {
    under_30_min: 0, "30_to_60_min": 1, "1_to_2_hours": 2, "2_plus_hours": 3
  };
  const driveRaw =
    (dailyTimeDrive[str(answers, "daily_time")] ?? 1) +
    (questDiffDrive[str(answers, "quest_difficulty")] ?? 1) +
    (scrollDrive[str(answers, "scroll_habit_baseline")] ?? 1) +
    (arr(answers, "your_why").includes("invest") ? 1 : 0) +
    (arr(answers, "your_why").includes("replace_scroll") ? 1 : 0) +
    (arr(answers, "your_why").includes("games") ? 1 : 0);

  // ── Groundwork ─────────────────────────────────────────────────────────
  const kc1Scores: Record<string, number> = {
    yes_explain: 4, rough_idea: 3, caesar: 2, both: 2, no_idea: 0
  };
  const kc2Scores: Record<string, number> = {
    knew: 4, yes_explain: 4, heard: 3, gas: 3,
    seriously: 1, never_wondered: 1, fascinating: 0, no_idea: 0
  };
  const groundworkRaw =
    (kc1Scores[str(answers, "knowledge_check_1")] ?? 2) +
    (kc2Scores[str(answers, "knowledge_check_2")] ?? 2);

  // ── Focus ──────────────────────────────────────────────────────────────
  const wanderFocus: Record<string, number> = {
    wander: 0, mixed: 1, guided: 3
  };
  const perfFocus: Record<string, number> = {
    not_really: 0, little_bit: 2
  };
  const learningStyles = arr(answers, "learning_style");
  const focusRaw =
    (wanderFocus[str(answers, "wander_vs_guided")] ?? 1) +
    (perfFocus[str(answers, "perfectionism")] ?? 0) +
    (learningStyles.includes("revisit") ? 1 : 0) +
    (learningStyles.includes("visual") ? 1 : 0);

  const BASE = 2;
  const curiosity = curiosityRaw + BASE;
  const drive = driveRaw + BASE;
  const groundwork = groundworkRaw + BASE;
  const focus = focusRaw + BASE;
  const total = curiosity + drive + groundwork + focus;

  return [
    { id: "curiosity", label: "Curiosity", percent: Math.round((curiosity / total) * 100), icon: "🔍" },
    { id: "drive", label: "Drive", percent: Math.round((drive / total) * 100), icon: "🔥" },
    { id: "groundwork", label: "Groundwork", percent: Math.round((groundwork / total) * 100), icon: "📚" },
    { id: "focus", label: "Focus", percent: Math.round((focus / total) * 100), icon: "🧭" }
  ].sort((a, b) => b.percent - a.percent);
};

const resolvePersonaKey = (answers: AnswersByKey): PersonaKey => {
  // Depth score (0–5): rabbit_hole (0–3) + quest_difficulty (0–2)
  const rabbitHoleDepth: Record<string, number> = {
    rarely: 0, sometimes: 1, often: 2, always: 3
  };
  const questDifficultyDepth: Record<string, number> = {
    explorer: 0, adventurer: 1, scholar: 2
  };

  const rabbitHole = typeof answers.rabbit_hole === "string" ? answers.rabbit_hole : "";
  const questDifficulty = typeof answers.quest_difficulty === "string" ? answers.quest_difficulty : "";

  const depthScore =
    (rabbitHoleDepth[rabbitHole] ?? 0) +
    (questDifficultyDepth[questDifficulty] ?? 0);

  const depth: "casual" | "moderate" | "deep" =
    depthScore <= 1 ? "casual" : depthScore <= 3 ? "moderate" : "deep";

  // Style score (0–3): wander_vs_guided (0–2) + perfectionism (0–1)
  const wanderStyle: Record<string, number> = {
    wander: 0, mixed: 1, guided: 2
  };
  const perfectionismStyle: Record<string, number> = {
    not_really: 0, little_bit: 1
  };

  const wander = typeof answers.wander_vs_guided === "string" ? answers.wander_vs_guided : "";
  const perfectionism = typeof answers.perfectionism === "string" ? answers.perfectionism : "";

  const styleScore =
    (wanderStyle[wander] ?? 1) +
    (perfectionismStyle[perfectionism] ?? 0);

  const style: "free" | "structured" = styleScore <= 1 ? "free" : "structured";

  if (depth === "casual" && style === "free") return "spark_chaser";
  if (depth === "casual" && style === "structured") return "apprentice";
  if (depth === "moderate" && style === "free") return "ranger";
  if (depth === "moderate" && style === "structured") return "navigator";
  if (depth === "deep" && style === "free") return "sage";
  return "devoted_scholar";
};

export const getPersonaData = (answers: AnswersByKey): PersonaData => ({
  ...PERSONAS[resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers)
});

export const resolvePersonaOverrideFromUrl = (
  urlLike: URL | string = window.location.href
): PersonaKey | null => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const override =
    resolvePersonaOverride(url.searchParams.get("persona")) ??
    resolvePersonaOverride(url.searchParams.get("persona_override"));
  return override;
};

export const getPersonaDataWithOverride = (
  answers: AnswersByKey,
  overridePersona: PersonaKey | null
): PersonaData => ({
  ...PERSONAS[overridePersona ?? resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers)
});

// ─────────────────────────────────────────────────────────────────────────────

const RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: "/hero-v2.webp",
    title: split(
      "Replace scrolling<br />with the best<br />History app",
      "Replace scrolling<br />with the best<br />History app"
    ),
    description: split([], []),
    ratingText: "3 million+ · curious minds",
    confirmLabel: "Continue"
  },
  {
    id: "age_group",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "age_group",
    title: "Who's taking the quest?",
    options: [
      { id: "18_24", label: "18-24", image: "/characters_1.webp" },
      { id: "25_34", label: "25-34", image: "/characters_2.webp" },
      { id: "35_44", label: "35-44", image: "/characters_3.webp" },
      { id: "45_plus", label: "45+", image: "/characters_4.webp" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "rabbit_hole",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "rabbit_hole",
    title: "How often do you fall down a rabbit hole learning about something random?",
    options: [
      { id: "rarely", label: "Rarely", emoji: "😐" },
      { id: "sometimes", label: "Sometimes", emoji: "🙂" },
      { id: "often", label: "More than I'd like to admit", emoji: "😄" },
      { id: "always", label: "It's basically a personality trait", emoji: "🤩" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "affirmation_1",
    order: 4,
    kind: "affirmation",
    progress: 20,
    title: "Lumi is a completely new way to learn.",
    description: [
      "Use those spare moments to fuel your curiosity with rewarding and fun bite-size information."
    ],
    mediaLabel: "Graphic placeholder",
    confirmLabel: "Show Me More →"
  },
  {
    id: "topic_interest_grid",
    order: 5,
    kind: "question",
    selection: "multi",
    answerKey: "topic_interest_grid",
    title: "What kinds of topics genuinely excite you?",
    helperText: "Pick as many as you like",
    minSelections: 1,
    options: [
      { id: "ancient_civilisations", label: "Ancient civilisations", emoji: "🏛️" },
      { id: "space_cosmos", label: "Space & Cosmos", emoji: "🚀" },
      { id: "psychology_mind", label: "Psychology & The mind", emoji: "🧠" },
      { id: "general_history", label: "General History", emoji: "📜" },
      { id: "nature_evolution", label: "Nature & Evolution", emoji: "🌿" },
      { id: "discoveries", label: "Inventions & Discoveries", emoji: "💡" },
      { id: "mysteries", label: "Mysteries", emoji: "🔮" },
      { id: "philosophy", label: "Philosophy & Thinking", emoji: "📜" },
      { id: "how_things_work", label: "How things actually work", emoji: "⚡" },
      { id: "human_body", label: "Humans & Health", emoji: "🧬" },
      { id: "culture_art_society", label: "Culture, art & music", emoji: "🎭" },
      { id: "business_marketing", label: "Business & Marketing", emoji: "💼" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "scroll_habit_baseline",
    order: 6,
    kind: "question",
    selection: "single",
    answerKey: "scroll_habit_baseline",
    title: "On a typical day, how much time do you spend mindlessly scrolling?",
    subtitle: "No judgement - this helps us personalise your starting pace.",
    options: [
      { id: "under_30_min", label: "Less than 30 minutes", emoji: "🟢" },
      { id: "30_to_60_min", label: "30-60 minutes", emoji: "🟡" },
      { id: "1_to_2_hours", label: "1-2 hours", emoji: "🟠" },
      { id: "2_plus_hours", label: "2+ hours", emoji: "🔴" }
    ],
    optionFeedback: {
      under_30_min: {
        text: "You're doing better than average — you'll have a solid foundation to be the most interesting person in the room.",
        variant: "positive"
      },
      "30_to_60_min": {
        text: "You're doing better than average — you'll have a solid foundation to be the most interesting person in the room.",
        variant: "positive"
      },
      "1_to_2_hours": {
        text: "Most people answer above where you are — you're not alone.",
        variant: "encouraging"
      },
      "2_plus_hours": {
        text: "Most people answer above where you are — you're not alone.",
        variant: "encouraging"
      }
    },
    confirmLabel: "Continue →"
  },
  {
    id: "daily_time",
    order: 7,
    kind: "question",
    selection: "single",
    answerKey: "daily_time",
    title: "How much time could you realistically spend each day with Lumi?",
    options: [
      { id: "2_min", label: "2 minutes - quick and sharp", emoji: "⚡" },
      { id: "5_min", label: "5 minutes - a quiet moment", emoji: "🕯️" },
      { id: "10_min", label: "10 minutes - proper focus", emoji: "📖" },
      { id: "pockets", label: "Whenever I find a pocket of time", emoji: "🗓️" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "knowledge_check_1",
    order: 8,
    kind: "question",
    selection: "single",
    answerKey: "knowledge_check_1",
    mediaLabel: split("Illustration: Roman Forum", "Illustration: Night sky / stars"),
    mediaImage: split("/roman_republic.webp", "/science_lightspeed.webp"),
    title: split(
      "Do you know why the Roman Republic actually fell?",
      "Do you know what a light year actually measures?"
    ),
    options: split(
      [
        { id: "yes_explain", label: "Yes, I could explain it", emoji: "✅" },
        { id: "rough_idea", label: "I have a rough idea", emoji: "🤔" },
        { id: "caesar", label: "Vaguely - something about Caesar?", emoji: "😅" },
        { id: "no_idea", label: "No idea honestly", emoji: "❌" }
      ],
      [
        { id: "yes_explain", label: "Yes, I could explain it", emoji: "✅" },
        { id: "rough_idea", label: "I have a rough idea", emoji: "🤔" },
        { id: "both", label: "Time? Distance? Both?", emoji: "😅" },
        { id: "no_idea", label: "No idea honestly", emoji: "❌" }
      ]
    ),
    confirmLabel: "Continue →"
  },
  {
    id: "knowledge_check_2",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "knowledge_check_2",
    mediaImage: split("/roman_gladiators.webp", "/science_ac.webp"),
    title: split(
      "Did you know gladiators were often celebrities, with fan clubs and endorsement deals?",
      "Do you know how air conditioning actually makes cold air?"
    ),
    options: split(
      [
        { id: "knew", label: "Yes, I knew that!", emoji: "🤩" },
        { id: "heard", label: "I kind of heard something like that", emoji: "🤔" },
        { id: "seriously", label: "Wait, seriously?", emoji: "😮" },
        { id: "fascinating", label: "No idea - but that's fascinating", emoji: "❌" }
      ],
      [
        { id: "yes_explain", label: "Yes, I could explain it", emoji: "✅" },
        { id: "gas", label: "Something to do with gas?", emoji: "🤔" },
        { id: "never_wondered", label: "I've genuinely never wondered", emoji: "😮" },
        { id: "no_idea", label: "No idea - now I'm curious", emoji: "❌" }
      ]
    ),
    confirmLabel: "Continue →"
  },
  {
    id: "wander_vs_guided",
    order: 10,
    kind: "question",
    selection: "single",
    answerKey: "wander_vs_guided",
    title: "When you're curious about something new, you prefer to...",
    options: [
      {
        id: "wander",
        label: "Wander freely and follow whatever catches my eye",
        emoji: "🗺️"
      },
      {
        id: "guided",
        label: "Be guided along a well-curated path",
        emoji: "🧭"
      },
      {
        id: "mixed",
        label: "A bit of structure with room to explore",
        emoji: "🔀"
      }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "learning_blocker",
    order: 11,
    kind: "question",
    selection: "single",
    answerKey: "learning_blocker",
    title: "What usually gets in the way of learning consistently?",
    options: [
      { id: "phone", label: "Too easy to pick up my phone instead", emoji: "📱" },
      { id: "time", label: "I genuinely don't feel like I have time", emoji: "⏰" },
      { id: "momentum", label: "I start things but lose momentum", emoji: "🔁" },
      { id: "grabbed", label: "Nothing has grabbed me enough yet", emoji: "😶" },
      { id: "forget", label: "I forget what I've actually learned", emoji: "🤔" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "affirmation_2",
    order: 12,
    kind: "affirmation",
    icon: "🏮",
    progress: 50,
    title: "Lumi's AI is already building your profile.",
    heroImage: "/lumi_building_profile.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    description: [
      "Based on your answers, we're mapping exactly which quest paths match how your brain likes to learn.",
      "<u>No generic content</u>. No wasted time ⏰."
    ],
    confirmLabel: "Keep Building →"
  },
  {
    id: "learning_style",
    order: 13,
    kind: "question",
    selection: "multi",
    answerKey: "learning_style",
    title: "What actually helps knowledge stick for you?",
    helperText: "Pick all that apply",
    minSelections: 1,
    options: [
      { id: "stories", label: "Short, well-written stories", emoji: "📖" },
      { id: "quick_fire", label: "Quick-fire questions", emoji: "🎯" },
      { id: "puzzles", label: "Challenges and puzzles", emoji: "🧩" },
      { id: "conversational", label: "Conversational explanations", emoji: "💬" },
      { id: "revisit", label: "Revisiting things over time", emoji: "🔁" },
      { id: "visual", label: "Visual comparisons and breakdowns", emoji: "📊" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "quest_difficulty",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "quest_difficulty",
    title: "Pick your quest difficulty",
    options: [
      {
        id: "explorer",
        label: "Explorer",
        description: "2 min/day · Bite-sized daily facts",
        emoji: "🌱"
      },
      {
        id: "adventurer",
        label: "Adventurer",
        description: "5 min/day · Facts + challenges",
        emoji: "⚔️",
        badge: "Most Popular"
      },
      {
        id: "scholar",
        label: "Scholar",
        description: "10 min/day · Full quest mode + deep dives",
        emoji: "🎓"
      }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "rapid_fire_1",
    order: 15,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_1",
    mediaLabel: split("Image: Julius Caesar", "Image: Pre-Industrial Engineering"),
    mediaImage: split("/history_julius_caesar.webp", "/science_preindustrial_engineering.webp"),
    title: split("Are you interested in the life of Julius Caesar?", "Are you interested in Pre-Industrial Engineering?"),
    options: yesNoOptions,
    confirmLabel: "Continue →"
  },
  {
    id: "rapid_fire_2",
    order: 16,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_2",
    mediaLabel: split("Image: Roman Colosseum", "Image: Practical chemistry"),
    mediaImage: split("/roman_gladiators.webp", "/science_chemicals.webp"),
    title: split(
      "Are you interested in gladiators and the Roman games?",
      "Are you interested in practical chemistry and how it affects us?"
    ),
    options: yesNoOptions,
    confirmLabel: "Continue →"
  },
  {
    id: "your_why",
    order: 17,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    title: "Why do you actually want to learn more?",
    helperText: "Pick all that apply",
    minSelections: 1,
    options: [
      { id: "curiosity", label: "Feed a genuine curiosity", emoji: "🧠" },
      { id: "conversation", label: "Hold my own in any conversation", emoji: "🗣️" },
      { id: "invest", label: "Invest in myself", emoji: "📈" },
      { id: "games", label: "I love games and progression", emoji: "🎮" },
      { id: "replace_scroll", label: "Replace pointless scrolling", emoji: "⏱️" },
      { id: "understand_world", label: "Actually understand the world", emoji: "🌍" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "generation_loading",
    order: 18,
    kind: "loading",
    progress: 85,
    title: "Crafting your Quest Map...",
    subtitle: "Lumi is calibrating your perfect challenge mix.",
    heroImage: "/lumi_building_questmap.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    loadingFact:
      "People who spend just 5 minutes learning daily retain knowledge 40% better than hour-long cramming sessions.",
    modalQuestion: {
      answerKey: "perfectionism",
      title: "Quick one - are you a perfectionist?",
      options: [
        { id: "little_bit", label: "A little bit" },
        { id: "not_really", label: "Not really" }
      ]
    },
    confirmLabel: "See My Results →"
  },
  {
    id: "persona_result",
    order: 19,
    kind: "result",
    icon: "🏮",
    title: "Your personalised profile is ready.",
    heroImageMarginBottom: 10,
    heroImageMask: "/mask_lite.webp",
    personaName: "The Ranger",
    description: [
      "You have a wide map and the instinct to follow wherever it leads.",
      "Lumi gives you the right trail to start on — after that, you're free to roam."
    ],
    questMap: [
      { id: "curiosity", label: "Curiosity", percent: 30, icon: "🔍" },
      { id: "drive", label: "Drive", percent: 28, icon: "🔥" },
      { id: "groundwork", label: "Groundwork", percent: 22, icon: "📚" },
      { id: "focus", label: "Focus", percent: 20, icon: "🧭" }
    ],
    confirmLabel: "Save My Quest Map →"
  },
  {
    id: "email_capture",
    order: 20,
    kind: "email",
    answerKey: "email",
    title: "Save your Quest Map",
    description: ["Your personalised profile will be held for 48 hours."],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Continue with Email →",
    trustNote: "No spam. Unsubscribe anytime.",
    quotes: [
      {
        text: "I've learned more in 2 weeks than years of casual interest.",
        author: "James T., 34"
      },
      {
        text: "It completely changed my daily commute. I actually look forward to it now.",
        author: "Sarah M., 29"
      }
    ]
  },
  {
    id: "founding_member",
    order: 21,
    kind: "payment",
    icon: "🏮",
    answerKey: "selected_plan",
    title: "Choose your plan",
    subtitle: "Founding Member",
    description: [
      "One focused daily session can reshape your learning habit in under a month.",
      "Pick the plan that fits your pace. You'll finish checkout securely on Stripe."
    ],
    plans: LUMI_PLANS,
    benefits: [
      "Founding pricing locked in",
      "Early launch access",
      "Your Quest Map and streak history saved"
    ],
    proofBullets: [
      "Personalised quests adapt to your answers and pace",
      "Short daily sessions designed to build a lasting learning habit",
      "Progress tracking keeps momentum visible and rewarding"
    ],
    testimonials: [
      {
        text: "I've learned more in 2 weeks than years of casual interest.",
        author: "James T., 34",
        weeksUsingApp: 7
      },
      {
        text: "It completely changed my daily commute. I actually look forward to it now.",
        author: "Sarah M., 29",
        weeksUsingApp: 12
      },
      {
        text: "I used to doom-scroll for an hour before bed. Now I do a Lumi quest instead. My sleep is better and I actually feel good about how I spent that time.",
        author: "Daniel K., 41",
        weeksUsingApp: 19
      },
      {
        text: "My kids started asking me questions at dinner because I was suddenly full of interesting things to talk about. That alone was worth it.",
        author: "Rachel O., 38",
        weeksUsingApp: 27
      },
      {
        text: "There's something about the quest format that makes it feel like a game rather than studying. I finished a topic on Roman engineering and genuinely couldn't stop.",
        author: "Priya N., 32",
        weeksUsingApp: 33
      }
    ],
    noChargeNote: "30-day money-back guarantee",
    securityNotes: ["Secure checkout via Stripe", "Cancel anytime"],
    confirmLabel: "Continue to Secure Checkout →",
    whyCardCopy:
      "Most people don't need more information, they need a system they can actually stick with. Lumi gives you that system in minutes a day."
  },
  {
    id: "checkout_complete",
    order: 22,
    kind: "affirmation",
    icon: "✅",
    title: "Confirming your checkout and preparing your email update.",
    description: [
      "If this takes longer than a moment, tap Try Again to refresh your Stripe status.",
      "Thanks for supporting Lumi."
    ],
    confirmLabel: "Done"
  }
];

const resolveStepByFlow = (step: RawStep, flow: FlowVariant): FunnelStep => {
  switch (step.kind) {
    case "welcome":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        ratingText: resolveByFlow(step.ratingText, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        heroSlides: step.heroSlides ? (resolveByFlow(step.heroSlides, flow) || undefined) : undefined,
        featurePills: step.featurePills
          ? (resolveByFlow(step.featurePills, flow) || undefined)
          : undefined,
        finePrint: step.finePrint ? resolveByFlow(step.finePrint, flow) : undefined
      };
    case "question":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        helperText: step.helperText ? resolveByFlow(step.helperText, flow) : undefined,
        mediaLabel: step.mediaLabel ? resolveByFlow(step.mediaLabel, flow) : undefined,
        mediaImage: step.mediaImage ? (resolveByFlow(step.mediaImage, flow) || undefined) : undefined,
        options: resolveByFlow(step.options, flow),
        optionFeedback: step.optionFeedback
      };
    case "affirmation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        heroImage: step.heroImage ? (resolveByFlow(step.heroImage, flow) || undefined) : undefined,
        mediaLabel: step.mediaLabel ? resolveByFlow(step.mediaLabel, flow) : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
    case "loading":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        loadingFact: resolveByFlow(step.loadingFact, flow),
        heroImage: step.heroImage ? (resolveByFlow(step.heroImage, flow) || undefined) : undefined
      };
    case "result":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        personaName: resolveByFlow(step.personaName, flow),
        description: resolveByFlow(step.description, flow),
        questMap: resolveByFlow(step.questMap, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
    case "email": {
      const { secondaryCta: rawSecondaryCta, ...emailRest } = step;
      return {
        ...emailRest,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        ...(rawSecondaryCta != null
          ? { secondaryCta: resolveByFlow(rawSecondaryCta, flow) }
          : {})
      };
    }
    case "payment":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        description: resolveByFlow(step.description, flow),
        benefits: resolveByFlow(step.benefits, flow),
        plans: resolveByFlow(step.plans, flow),
        proofBullets: resolveByFlow(step.proofBullets, flow),
        testimonials: resolveByFlow(step.testimonials, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        whyCardCopy: resolveByFlow(step.whyCardCopy, flow)
      };
  }
};

const getEnabledRawSteps = (): RawStep[] => {
  return RAW_STEPS.filter((step) => {
    if (step.id === "email_capture") {
      return config.enableEmailCapture;
    }

    return true;
  });
};

export const getFlowSteps = (flow: FlowVariant): FunnelStep[] => {
  return getEnabledRawSteps().map((step) => resolveStepByFlow(step, flow));
};

export const TOTAL_STEPS = getEnabledRawSteps().length;

export const FLOW_META: Record<FlowVariant, { name: string; slug: string }> = {
  a: { name: "Roman History", slug: "roman" },
  b: { name: "General Science", slug: "science" }
};

export type FunnelSection = {
  label: string;
  /** inclusive 0-based start index */
  start: number;
  /** inclusive 0-based end index */
  end: number;
};

const BASE_FUNNEL_SECTIONS: FunnelSection[] = [
  { label: "Profile", start: 0, end: 6 },
  { label: "Personality", start: 7, end: 13 },
  { label: "Personalise", start: 14, end: 21 }
];

export const getFunnelSections = (steps: FunnelStep[]): FunnelSection[] => {
  const maxIndex = steps.length - 1;
  return BASE_FUNNEL_SECTIONS.map((section, index) => ({
    label: section.label,
    start: Math.min(section.start, maxIndex),
    end: index === BASE_FUNNEL_SECTIONS.length - 1 ? maxIndex : Math.min(section.end, maxIndex)
  })).filter((section) => section.start <= section.end);
};

export const computeSectionProgress = (currentIndex: number, sections: FunnelSection[]): number[] => {
  return sections.map(({ start, end }) => {
    const size = end - start + 1;
    const completed = Math.min(Math.max(currentIndex - start, 0), size);
    return completed / size;
  });
};
